import { useState } from "react"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "react-router-dom"
import { useCreateCourse, useUpdateCourse } from "../hooks/useCourses"
import { usePersonas } from "@/features/personas/hooks/usePersonas"
import {
  createCourseSchema,
  type CreateCourseFormData,
  type CreateCourseFormInput,
  type Course,
} from "../types"
import { Input } from "@/components/ui/Input"
import { Alert } from "@/components/ui/Alert"
import { Button } from "@/components/ui/Button"
import { useTranslation } from "react-i18next"

const MODE_OPTIONS: {
  value: "free" | "rag" | "hybrid"
  labelKey: string
  descKey: string
}[] = [
  {
    value: "free",
    labelKey: "courses.mode_options.free_label",
    descKey: "courses.mode_options.free_desc",
  },
  {
    value: "rag",
    labelKey: "courses.mode_options.rag_label",
    descKey: "courses.mode_options.rag_desc",
  },
  {
    value: "hybrid",
    labelKey: "courses.mode_options.hybrid_label",
    descKey: "courses.mode_options.hybrid_desc",
  },
]

interface CreateCourseModalProps {
  open: boolean
  onClose: () => void
  editCourse?: Course
}

export function CreateCourseModal({ open, onClose, editCourse }: CreateCourseModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [step, setStep] = useState<1 | 2>(1)
  const [apiError, setApiError] = useState<string | null>(null)

  const isEditMode = !!editCourse

  const { data: personas } = usePersonas()
  const createMutation = useCreateCourse()
  const updateMutation = useUpdateCourse()

  const {
    register,
    handleSubmit,
    control,
    trigger,
    formState: { errors },
  } = useForm<CreateCourseFormInput, unknown, CreateCourseFormData>({
    resolver: zodResolver(createCourseSchema),
    defaultValues: {
      title: "",
      subject: "",
      description: "",
      mode: "free",
      language: "fr",
      persona_id: null,
    },
  })

  // La modale est montée à l'ouverture et démontée à la fermeture : son état
  // repart de zéro à chaque fois, sans effet de réinitialisation.

  async function handleNextStep() {
    const valid = await trigger(["title", "subject", "description", "language"])
    if (valid) setStep(2)
  }

  async function onSubmit(data: CreateCourseFormData) {
    setApiError(null)
    try {
      if (isEditMode && editCourse) {
        await updateMutation.mutateAsync({ id: editCourse.id, data })
        onClose()
      } else {
        const res = await createMutation.mutateAsync(data)
        onClose()
        navigate(`/courses/${res.data.data.id}`)
      }
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { error?: { message?: string } } } }
      setApiError(
        anyErr?.response?.data?.error?.message ?? t("common.error")
      )
    }
  }

  const isPending = isEditMode ? updateMutation.isPending : createMutation.isPending

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={isEditMode ? t("courses.modal.edit_label") : t("courses.modal.create_label")}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              {isEditMode ? t("courses.modal.edit_title") : t("courses.modal.create_title")}
            </h2>
            <p className="mt-0.5 text-sm text-neutral-600">
              {t("courses.modal.step", { step })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("courses.modal.close")}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-neutral-600 transition-colors hover:bg-surface-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <path
                d="M3 3l12 12M15 3L3 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Step indicator */}
        <div className="mb-6 flex gap-1.5">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={[
                "h-1 flex-1 rounded-full transition-colors",
                s <= step ? "bg-mindeia-primary" : "bg-surface-200",
              ].join(" ")}
            />
          ))}
        </div>

        {apiError && (
          <Alert variant="error" className="mb-4">
            {apiError}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* ── Step 1 ── */}
          {step === 1 && (
            <div className="flex flex-col gap-4">
              <Input
                label={t("courses.modal.title_label")}
                placeholder={t("courses.modal.title_placeholder")}
                error={errors.title?.message && t(errors.title.message)}
                {...register("title")}
              />

              <Input
                label={t("courses.modal.subject_label")}
                placeholder={t("courses.modal.subject_placeholder")}
                error={errors.subject?.message && t(errors.subject.message)}
                {...register("subject")}
              />

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="course-description"
                  className="text-sm font-medium text-surface-800"
                >
                  {t("courses.modal.description_label")}{" "}
                  <span className="font-normal text-neutral-600">
                    {t("courses.modal.optional")}
                  </span>
                </label>
                <textarea
                  id="course-description"
                  rows={3}
                  placeholder={t("courses.modal.description_placeholder")}
                  className={[
                    "rounded-md border px-3 py-2 text-sm text-surface-900",
                    "placeholder:text-surface-200 resize-none",
                    "focus:outline-none focus:ring-2 focus:ring-mindeia-primary focus:border-mindeia-primary",
                    "transition-colors duration-150",
                    errors.description ? "border-red-400 bg-red-50" : "border-surface-200 bg-white",
                  ].join(" ")}
                  {...register("description")}
                />
                {errors.description && (
                  <p role="alert" className="text-xs text-red-600">
                    {t(errors.description.message ?? "")}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="course-language"
                  className="text-sm font-medium text-surface-800"
                >
                  {t("courses.modal.language_label")}
                </label>
                <select
                  id="course-language"
                  className="rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-mindeia-primary focus:outline-none focus:ring-2 focus:ring-mindeia-primary"
                  style={{ minHeight: "44px" }}
                  {...register("language")}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>
            </div>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-surface-800">
                  {t("courses.modal.mode_label")}
                </span>
                <Controller
                  name="mode"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-col gap-2">
                      {MODE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          aria-pressed={field.value === opt.value}
                          onClick={() => field.onChange(opt.value)}
                          className={[
                            "flex flex-col items-start rounded-xl border-2 px-4 py-3 text-left transition-all",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary",
                            field.value === opt.value
                              ? "border-mindeia-primary bg-mindeia-primary-light"
                              : "border-surface-200 bg-white hover:border-mindeia-primary",
                          ].join(" ")}
                          style={{ minHeight: "44px" }}
                        >
                          <span
                            className={[
                              "text-sm font-semibold",
                              field.value === opt.value
                                ? "text-mindeia-primary"
                                : "text-neutral-800",
                            ].join(" ")}
                          >
                            {t(opt.labelKey)}
                          </span>
                          <span className="mt-0.5 text-xs text-neutral-600">
                            {t(opt.descKey)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                />
                {errors.mode && (
                  <p role="alert" className="text-xs text-red-600">
                    {t(errors.mode.message ?? "")}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="course-persona"
                  className="text-sm font-medium text-surface-800"
                >
                  {t("courses.modal.persona_label")}{" "}
                  <span className="font-normal text-neutral-600">
                    {t("courses.modal.optional")}
                  </span>
                </label>
                <select
                  id="course-persona"
                  className="rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-mindeia-primary focus:outline-none focus:ring-2 focus:ring-mindeia-primary"
                  style={{ minHeight: "44px" }}
                  {...register("persona_id")}
                >
                  <option value="">{t("courses.modal.persona_none")}</option>
                  {personas?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Footer buttons */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={step === 1 ? onClose : () => setStep(1)}
            >
              {step === 1 ? t("common.cancel") : t("common.previous")}
            </Button>

            {/*
              Les deux boutons portent des clés distinctes. Sans elles, React
              réutilise le même nœud DOM et le bouton « Suivant » devient un
              bouton de soumission avant que le navigateur n'exécute l'action
              par défaut du clic : le cours partait dès la première étape, sans
              que l'utilisateur voie jamais la seconde.
            */}
            {step === 1 ? (
              <Button key="next-step" type="button" onClick={handleNextStep}>
                {t("courses.modal.next")}
              </Button>
            ) : (
              <Button key="submit-course" type="submit" isLoading={isPending} disabled={isPending}>
                {isEditMode ? t("courses.modal.submit_edit") : t("courses.modal.submit_create")}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}
